import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { motion } from 'framer-motion';
import { Camera, Loader2, ArrowLeft, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error(t('auth.emailRequired'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success(language === 'pl' 
        ? 'Link do resetu hasła został wysłany' 
        : 'Password reset link has been sent'
      );
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      toast.error(t('auth.passwordRequired'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(language === 'pl' ? 'Hasła nie są zgodne' : 'Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error(t('auth.passwordMinLength'));
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setResetComplete(true);
      toast.success(language === 'pl' ? 'Hasło zostało zmienione' : 'Password has been reset');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  // Reset complete view
  if (resetComplete) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm text-center"
        >
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold mb-2">
            {language === 'pl' ? 'Hasło zmienione!' : 'Password Reset!'}
          </h1>
          <p className="text-muted-foreground mb-6">
            {language === 'pl' 
              ? 'Możesz teraz zalogować się nowym hasłem' 
              : 'You can now log in with your new password'
            }
          </p>
          <Button onClick={() => navigate('/login')} className="w-full">
            {t('auth.login')}
          </Button>
        </motion.div>
      </div>
    );
  }

  // Token provided - show reset form
  if (token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          <Link to="/" className="flex items-center gap-2 mb-12 justify-center">
            <Camera className="w-6 h-6" />
            <span className="font-bold text-lg">Focus Photo</span>
          </Link>
          
          <h1 className="text-2xl font-bold mb-2 text-center">
            {language === 'pl' ? 'Ustaw nowe hasło' : 'Set New Password'}
          </h1>
          <p className="text-muted-foreground mb-8 text-center">
            {language === 'pl' 
              ? 'Wprowadź nowe hasło dla swojego konta' 
              : 'Enter a new password for your account'
            }
          </p>

          <form onSubmit={handleResetPassword} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="newPassword">
                {language === 'pl' ? 'Nowe hasło' : 'New Password'}
              </Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12"
                data-testid="new-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                {language === 'pl' ? 'Potwierdź hasło' : 'Confirm Password'}
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12"
                data-testid="confirm-password-input"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full h-12" 
              disabled={loading}
              data-testid="reset-password-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                language === 'pl' ? 'Zmień hasło' : 'Reset Password'
              )}
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Email sent view
  if (sent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm text-center"
        >
          <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold mb-2">
            {language === 'pl' ? 'Sprawdź email' : 'Check Your Email'}
          </h1>
          <p className="text-muted-foreground mb-6">
            {language === 'pl' 
              ? `Wysłaliśmy link do resetu hasła na ${email}` 
              : `We've sent a password reset link to ${email}`
            }
          </p>
          <Button variant="outline" onClick={() => navigate('/login')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'pl' ? 'Wróć do logowania' : 'Back to Login'}
          </Button>
        </motion.div>
      </div>
    );
  }

  // Request reset form
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        <Link to="/" className="flex items-center gap-2 mb-12 justify-center">
          <Camera className="w-6 h-6" />
          <span className="font-bold text-lg">Focus Photo</span>
        </Link>
        
        <h1 className="text-2xl font-bold mb-2 text-center">{t('auth.forgotPassword')}</h1>
        <p className="text-muted-foreground mb-8 text-center">
          {language === 'pl' 
            ? 'Wprowadź email, a wyślemy Ci link do resetu hasła' 
            : 'Enter your email and we\'ll send you a reset link'
          }
        </p>

        <form onSubmit={handleRequestReset} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email">{t('auth.email')}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              className="h-12"
              data-testid="forgot-email-input"
            />
          </div>

          <Button 
            type="submit" 
            className="w-full h-12" 
            disabled={loading}
            data-testid="forgot-submit-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              language === 'pl' ? 'Wyślij link' : 'Send Reset Link'
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link 
            to="/login" 
            className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {language === 'pl' ? 'Wróć do logowania' : 'Back to Login'}
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
